import { colour, progressShade, radius, space, text } from '../../design/tokens.ts';
import type { BoardAnswer, BoardCell } from './types.ts';

/**
 * The heatmap: one cell per VC pass, shaded by how far along it is.
 *
 * Up to about 2,500 cells, so each one is a `<span>` with three style values
 * and nothing else — no component, no handler per cell. The server already sent
 * a compact cell (`{v, s, p, st, ac, bl, pl}`) rather than the whole pass, which
 * is the only reason this can draw the lot.
 *
 * The legend comes from the server too, so the key and the cells cannot
 * disagree. Nothing is colour alone: the title attribute says what each cell
 * is, and the whole grid carries one `aria-label` because 2,500 announced cells
 * would be unusable with a screen reader.
 */
export function Heatmap({
  answer,
  onPick
}: {
  answer: BoardAnswer;
  onPick?: (cell: BoardCell) => void;
}) {
  const byVc = answer.view === 'by_vc';
  const rowKeys = [...new Set(answer.cells.map((c) => (byVc ? c.v : c.s)))].sort();
  const colKeys = [...new Set(answer.cells.map((c) => (byVc ? c.s : c.v)))].sort();
  const label = (key: string, isRow: boolean): string => {
    const asGroup = answer.groups.find((g) => g.key === key);
    if (asGroup) return asGroup.label;
    return isRow === byVc ? key : key.slice(-4);
  };

  const held = new Map<string, BoardCell>();
  for (const cell of answer.cells) held.set(`${cell.v}|${cell.s}`, cell);

  const steps = answer.columns.length;

  if (answer.cells.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.control }}>
      <div style={{ overflowX: 'auto' }}>
        <div
          role="img"
          aria-label={`Grid of ${answer.cells.length} VC passes, shaded by how far each has progressed`}
          style={{
            minWidth: 560,
            display: 'grid',
            gridTemplateColumns: `minmax(90px, auto) repeat(${colKeys.length}, minmax(14px, 1fr))`,
            gap: 2
          }}
        >
          <span />
          {colKeys.map((key) => (
            <span
              key={key}
              title={label(key, false)}
              style={{
                ...text.label,
                color: colour.inkMuted,
                writingMode: 'vertical-rl',
                textOrientation: 'mixed',
                maxHeight: 84,
                overflow: 'hidden',
                justifySelf: 'center'
              }}
            >
              {label(key, false)}
            </span>
          ))}

          {rowKeys.map((rowKey) => (
            <Row
              key={rowKey}
              rowKey={rowKey}
              colKeys={colKeys}
              byVc={byVc}
              held={held}
              steps={steps}
              label={label(rowKey, true)}
              {...(onPick ? { onPick } : {})}
            />
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.card }}>
        {answer.legend.map((entry) => (
          <span
            key={entry.flag}
            style={{ display: 'flex', alignItems: 'center', gap: 6, ...text.note, color: colour.inkSoft }}
          >
            <Swatch flag={entry.flag} steps={steps} />
            {entry.says}
          </span>
        ))}
      </div>
    </div>
  );
}

function Swatch({ flag, steps }: { flag: string; steps: number }) {
  const base: React.CSSProperties = {
    width: 16,
    height: 16,
    borderRadius: 3,
    flex: 'none',
    background: colour.cellEmpty,
    border: `1px solid ${colour.edge}`
  };
  if (flag === 'p') return <span style={{ ...base, background: progressShade(steps - 1, steps) }} />;
  if (flag === 'st') return <span style={{ ...base, border: `2px solid ${colour.stale}` }} />;
  if (flag === 'ac') return <span style={{ ...base, border: `2px dotted ${colour.stale}` }} />;
  if (flag === 'pl')
    return <span style={{ ...base, border: `2px dashed ${colour.accent}`, background: colour.accentWash }} />;
  return <span style={{ ...base, background: colour.accentWash, border: `1px solid ${colour.accent}` }} />;
}

function Row({
  rowKey,
  colKeys,
  byVc,
  held,
  steps,
  label,
  onPick
}: {
  rowKey: string;
  colKeys: string[];
  byVc: boolean;
  held: Map<string, BoardCell>;
  steps: number;
  label: string;
  onPick?: (cell: BoardCell) => void;
}) {
  return (
    <>
      <span
        style={{
          ...text.note,
          color: colour.inkSoft,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          alignSelf: 'center'
        }}
      >
        {label}
      </span>
      {colKeys.map((colKey) => {
        const cell = held.get(byVc ? `${rowKey}|${colKey}` : `${colKey}|${rowKey}`);
        if (!cell) {
          // Blank: this VC is not used in that sequence. Not a gap in the data
          // — a pass that does not exist.
          return <span key={colKey} style={{ height: 18, background: 'transparent' }} />;
        }
        const border = cell.pl
          ? `2px dashed ${colour.accent}`
          : cell.ac
            ? `2px dotted ${colour.stale}`
            : cell.st
              ? `2px solid ${colour.stale}`
              : cell.bl
                ? `1px solid ${colour.accent}`
                : `1px solid rgba(27, 28, 30, 0.08)`;
        return (
          <span
            key={colKey}
            {...(onPick ? { onClick: () => onPick(cell), role: 'button', tabIndex: -1 } : {})}
            title={`${cell.v} · ${cell.pl ? 'VC not created yet' : cell.bl ? 'blocked' : cell.ac ? 'stale, kept as it is' : cell.st ? 'stale' : `step ${cell.p + 1} of ${steps}`}`}
            style={{
              height: 18,
              borderRadius: radius.item - 3,
              background: cell.pl || cell.bl ? colour.accentWash : progressShade(cell.p, steps),
              border,
              cursor: onPick ? 'pointer' : 'default'
            }}
          />
        );
      })}
    </>
  );
}
